import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, MailCheck, Send } from 'lucide-react';
import { api } from '@/api/client';

interface Preview {
  total: number;
  sendable: number;
  skippedInvalidEmail: number;
  sample: Array<{ id: number; companyName: string; email: string; status: string }>;
}

/**
 * One-time (repeatable) campaign that mails every unverified merchant a one-click verification link.
 *
 * Deliberately preview-first: the send is thousands of real emails to real customers, and "412
 * merchants will be emailed" is something to read before pressing the button, not to discover from
 * the replies. The batch cap exists so the first run can be a handful of accounts.
 */
export function EmailVerificationCampaign({ onDone }: { onDone?: () => void }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [useLimit, setUseLimit] = useState(true);
  const [limit, setLimit] = useState(10);
  const [result, setResult] = useState<any>(null);

  const { data: preview, isLoading } = useQuery<Preview>({
    queryKey: ['merchant-email-campaign-preview', includeInactive],
    queryFn: async () => (await api.get('/admin/merchants/email-verification/preview', { params: { includeInactive } })).data,
  });

  const send = useMutation({
    mutationFn: async () => (await api.post('/admin/merchants/email-verification/send', {
      includeInactive,
      ...(useLimit ? { limit } : {}),
    }, {
      // The server sends sequentially with a deliberate gap between messages so the sending domain
      // is not rate-limited. A full run is minutes of work, far past the client's 30s default.
      timeout: 30 * 60_000,
    })).data,
    onSuccess: (r) => {
      setResult(r);
      toast.success(`${r.sent} verification email${r.sent === 1 ? '' : 's'} sent`);
      onDone?.();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Campaign failed'),
  });

  if (isLoading) {
    return <div className="py-10 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" size={22} /></div>;
  }

  const willSend = useLimit ? Math.min(limit, preview?.sendable ?? 0) : (preview?.sendable ?? 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Unverified" value={preview?.total ?? 0} />
        <Stat label="Can be emailed" value={preview?.sendable ?? 0} tone="good" />
        <Stat label="No valid email" value={preview?.skippedInvalidEmail ?? 0} tone={preview?.skippedInvalidEmail ? 'warn' : undefined} />
      </div>

      <div className="space-y-2.5">
        <label className="flex items-center gap-2.5 text-sm text-slate-700">
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} className="rounded" />
          Also include <b>INACTIVE</b> accounts
          <span className="text-xs text-slate-400">(dormant inboxes — more likely to bounce)</span>
        </label>
        <label className="flex items-center gap-2.5 text-sm text-slate-700">
          <input type="checkbox" checked={useLimit} onChange={(e) => setUseLimit(e.target.checked)} className="rounded" />
          Send to only the first
          <input type="number" min={1} value={limit} disabled={!useLimit}
            onChange={(e) => setLimit(Math.max(1, Number(e.target.value) || 1))}
            className="input w-20 py-1 text-sm" />
          merchants
        </label>
        <p className="text-xs text-slate-400 pl-6">
          Leave this on for the first run — check the email arrives and reads correctly before mailing everyone.
        </p>
      </div>

      {preview?.sample?.length ? (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-500 uppercase">First few recipients</div>
          <div className="max-h-40 overflow-y-auto divide-y divide-slate-100">
            {preview.sample.map((m) => (
              <div key={m.id} className="px-3 py-1.5 text-sm flex items-center gap-2">
                <span className="text-slate-400 text-xs font-mono w-12 shrink-0">#{m.id}</span>
                <span className="flex-1 truncate text-slate-700">{m.companyName}</span>
                <span className="text-xs text-slate-500 truncate max-w-[45%]">{m.email}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-500 text-center py-4">Every merchant has already verified their email. Nothing to send.</div>
      )}

      {result && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
          <div className="font-semibold text-slate-800 flex items-center gap-1.5"><MailCheck size={15} className="text-success" /> Campaign finished</div>
          <div className="text-slate-600">{result.sent} sent · {result.failed} failed · {result.skipped} skipped (no valid email)</div>
          {result.failures?.length > 0 && (
            <div className="mt-2 max-h-28 overflow-y-auto text-xs text-rose-600 space-y-0.5">
              {result.failures.map((f: any) => <div key={f.id}>#{f.id} {f.email} — {f.error}</div>)}
            </div>
          )}
        </div>
      )}

      <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
        <AlertTriangle size={15} className="shrink-0 mt-0.5" />
        <span>
          These are real emails to real customers. Merchants who do not click stay unverified — nothing is
          switched off for them. Links stay valid for 30 days and you can run this again for the ones who
          did not respond.
        </span>
      </div>

      <button onClick={() => send.mutate()} disabled={send.isPending || willSend === 0}
        className="w-full px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2">
        {send.isPending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {send.isPending ? 'Sending — this can take several minutes…' : `Send to ${willSend} merchant${willSend === 1 ? '' : 's'}`}
      </button>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'good' | 'warn' }) {
  const color = tone === 'good' ? 'text-success' : tone === 'warn' ? 'text-amber-600' : 'text-slate-900';
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-center">
      <div className={`text-2xl font-bold tabular-nums ${color}`}>{value.toLocaleString('en-IN')}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
